import { useState } from 'react';
import { Popover } from '@mui/material';

import { ListFilterOption } from '@common/ui/ListFilters/common';
import ListFilterMultiSelect from '@common/ui/ListFilters/ListFilterMultiSelect';
import ListFilterSelectChipTrigger from '@common/ui/ListFilters/ListFilterSelectChipTrigger';

import { useGetAssessmentReportPermissionsFilterOptions } from '@StandingAccess/services/standingAccessQueries';
import { ASSESSMENT_REPORT_PERMISSIONS_FILTER } from '@StandingAccess/types';

interface AssessmentReportPermissionsFilterProps {
  reportId: string;
  selected: string[];
  onSelect: (value: string[]) => void;
}

function useOptions({
  reportId,
  selected,
  enabled,
  searchValue,
}: Pick<AssessmentReportPermissionsFilterProps, 'reportId' | 'selected'> & {
  enabled?: boolean;
  searchValue?: string;
}) {
  const {
    data,
    isFetched: isFetchedOptions,
    fetchNextPage,
    isFetchingNextPage,
  } = useGetAssessmentReportPermissionsFilterOptions({
    reportId,
    search: searchValue,
    enabled: !!enabled,
  });

  const permissions = data?.pages.flatMap((p) => p.permissions) || [];

  const options: ListFilterOption[] = permissions.map((r) => ({
    value: r.id,
    label: r.name,
  }));

  const selectedOptions = options.filter((o) => selected.includes(o.value));

  const isFetched = enabled ? isFetchedOptions : true;

  return {
    options,
    selectedOptions,
    isFetched,
    isFetching: isFetchingNextPage,
    fetchNextPage,
  };
}

function useOpenClose() {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | undefined>(undefined);

  const handleOpen = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(undefined);
  };

  return { opened: !!anchorEl, anchorEl, handleOpen, handleClose };
}

function useSearchValue() {
  const [searchValue, setSearchValue] = useState('');

  const clearSearch = () => {
    setSearchValue('');
  };

  return { searchValue, setSearchValue, clearSearch };
}

function useSelectHandler({ onSelect }: Pick<AssessmentReportPermissionsFilterProps, 'onSelect'>) {
  const onSelectHandler = (value: ListFilterOption[]) => {
    onSelect(value.map((v) => v.value));
  };

  const onClear = () => {
    onSelect([]);
  };

  return { onSelectHandler, onClear };
}

export default function AssessmentReportPermissionsFilter({
  reportId,
  selected,
  onSelect,
}: AssessmentReportPermissionsFilterProps) {
  const { onSelectHandler, onClear } = useSelectHandler({ onSelect });
  const { opened, anchorEl, handleOpen, handleClose } = useOpenClose();
  const { searchValue, setSearchValue, clearSearch } = useSearchValue();
  const { options, selectedOptions, isFetched, isFetching, fetchNextPage } = useOptions({
    reportId,
    selected,
    searchValue,
    enabled: opened || !!selected.length,
  });

  return (
    <>
      <ListFilterSelectChipTrigger
        active={opened}
        label={ASSESSMENT_REPORT_PERMISSIONS_FILTER.label}
        value={selectedOptions}
        onClear={onClear}
        onClick={(e) => {
          handleOpen(e);
          clearSearch();
        }}
        loading={!isFetched}
      />
      <Popover
        open={opened}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
      >
        <ListFilterMultiSelect
          width={300}
          options={options}
          value={selectedOptions}
          onSelect={onSelectHandler}
          enableSearch
          searchValue={searchValue}
          onSearch={setSearchValue}
          title={`Select ${ASSESSMENT_REPORT_PERMISSIONS_FILTER.label} Filter`}
          isFetching={!isFetched}
          isLoading={isFetching}
          onBottomReached={fetchNextPage}
        />
      </Popover>
    </>
  );
}
